<template>
	<div>
		<LUserFooter
			:name="displayName"
			:initial="initials"
			:subtitle="subtitle"
			:menu-items="menuItems"
		/>

		<LConfirmModal
			v-model:open="isSignOutConfirmOpen"
			title="Sign out?"
			description="You'll need to sign in again to use Lotus."
			confirm-label="Sign out"
			confirm-variant="destructive"
			cancel-label="Cancel"
			@confirm="onSignOutConfirmed"
		/>
	</div>
</template>










<script setup>
/**
 * The sidebar user menu: who is signed in, and the three things they can do
 * from anywhere in the portal - open their profile, read what Lotus captures,
 * or sign out.
 *
 * Signing out is confirmed first (§ "Sign-out confirmation") and then goes
 * through /helpers/session.js, which is the only place a session is torn down.
 */
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import globalStore from "/globalStore.js";
import { signOut, SIGN_OUT_REASON } from "/helpers/session.js";
import { getProfile } from "/my-profile/profile-data.js";
import { formatFirmLine } from "/my-profile/profile-formatting.js";
import { activeMessages } from "/system-messages/system-messages-data.js";
import { LUserFooter, LConfirmModal } from '/components2/index.js';

const router = useRouter();





//-- Data/Reactive State
const currentUser = ref(globalStore.state.currentUser);
const isSignOutConfirmOpen = ref(false);

// The firm and the person's role in it, for the line under their name. Loaded
// from the profile record, which is cached for the visit — the profile page and
// this menu share the one call.
const firmLine = ref(null);





//-- Computed Properties

// First name only. A person doesn't need to be told their own surname.
const displayName = computed(() => currentUser.value.firstName ? currentUser.value.firstName : currentUser.value.displayName);


const initials = computed(() =>
{
	const first = currentUser.value.firstName ? currentUser.value.firstName.substring(0, 1) : "";
	const last = currentUser.value.lastName ? currentUser.value.lastName.substring(0, 1) : "";

	return (first + last).toUpperCase();
});


// Null until the profile arrives, so the row never shows an empty second line.
const subtitle = computed(() => firmLine.value ? firmLine.value : null);


// The banner list is loaded by the shell (SystemMessageBanners), so this reads it rather
// than fetching again — and reacts when the person dismisses one.
const messagesLabel = computed(() =>
{
	const count = activeMessages.value.length;

	return count > 0 ? "Messages (" + count + ")" : "Messages";
});


const menuItems = computed(() =>
{
	const items = [
		{ label: "Profile", icon: "user", onSelect: () => goTo("/my-profile") },

		// Every signed-in person can be sent a system message, so this is unconditional.
		// The count is the banners currently showing them, so the row reads as "you have
		// something waiting" rather than as a total that never goes down.
		{ label: messagesLabel.value, icon: "envelope", onSelect: () => goTo("/my-profile/messages") },
	];

	if (globalStore.currentUser.hasFeatureAccess("Timesheets.Enabled") && globalStore.currentUser.hasFeatureAccess("Timesheets.Core"))
	{
		items.push({ label: "What Lotus captures", icon: "shield-halved", onSelect: () => goTo("/help/what-lotus-captures") });
	}

	items.push({ separator: true });
	items.push({ label: "Sign out", icon: "arrow-right-from-bracket", destructive: true, onSelect: () => openSignOutConfirm() });

	return items;
});





//-- Methods

// Always have an init() that get's called from onMounted()
const init = async () =>
{
	currentUser.value = globalStore.state.currentUser;

	const profile = await getProfile();
	if (profile == null) return;

	firmLine.value = formatFirmLine(profile.Firm.Name, profile.Identity.JobTitle);
};


const goTo = async (path) =>
{
	await router.push({ path: path });
};


const openSignOutConfirm = () =>
{
	isSignOutConfirmOpen.value = true;
};


const onSignOutConfirmed = () =>
{
	signOut(SIGN_OUT_REASON.SignedOut);
};





//-- Lifecycle Hooks

// Always have onMounted() and it must call init().
onMounted(async () =>
{
	await init();
});
</script>
